/**
 * ManagePage — Displays license status, device list, and bundle download.
 * Protected route — requires active portal session.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { LogOut, RefreshCw, Calendar, AlertTriangle, XCircle } from 'lucide-react';
import { usePortal } from '../context/PortalContext';
import { StatusBadge } from '../components/StatusBadge';
import { DeviceList } from '../components/DeviceList';
import { BundleActions } from '../components/BundleActions';
import { Notification } from '../components/Notification';
import { PRODUCT_DISPLAY, getProductDisplayByProductId } from '../config/products';
import { usePortalPageMeta } from '../hooks/usePortalPageMeta';

function formatDate(isoStr) {
    if (!isoStr) return '—';
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(new Date(isoStr));
}

function isMaintenanceExpired(maintenanceEnd) {
    if (!maintenanceEnd) return false;
    return new Date(maintenanceEnd) < new Date();
}

function ExistingLicenseActivationPanel({
    productDisplay,
    deviceCode,
    devices,
    isRevoked,
    onPrepareDevice,
    onIssueBundle,
    trackingContext,
}) {
    const activationName = productDisplay?.activationName || 'this extension';
    const isRegistered = devices.some((device) => device.device_code === deviceCode);

    return (
        <section className="bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-900/50 rounded-xl p-5 space-y-4">
            <div>
                <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                    Activate {activationName} on this device
                </h2>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                    Your license was found. The portal will register this device, create a fresh
                    activation code, and send it to the extension.
                </p>
            </div>

            <div className="rounded-lg bg-white/70 dark:bg-zinc-900/60 border border-indigo-100 dark:border-indigo-900/50 px-3 py-2">
                <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Device code</p>
                <code className="mt-1 block break-all text-xs text-zinc-700 dark:text-zinc-300">
                    {deviceCode}
                </code>
                {isRegistered && (
                    <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
                        This device is already registered on the license.
                    </p>
                )}
            </div>

            <BundleActions
                onIssueBundle={onIssueBundle}
                onBeforeIssueBundle={onPrepareDevice}
                hasDevices={devices.length > 0 || Boolean(deviceCode)}
                isRevoked={isRevoked}
                activationTarget={
                    productDisplay?.extensionId
                        ? {
                              extensionId: productDisplay.extensionId,
                              productName: activationName,
                          }
                        : null
                }
                trackingContext={trackingContext}
            />
        </section>
    );
}

export function ManagePage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const deviceCodeParam = searchParams.get('device_code') || '';
    const intentParam = searchParams.get('intent') || '';
    const productParam = searchParams.get('product') || '';
    const feature = searchParams.get('feature') || '';
    const source = searchParams.get('source') || '';
    const { state, actions } = usePortal();
    const { license, devices, isLoading, error: contextError } = state;
    const productDisplay =
        getProductDisplayByProductId(license?.productId) || PRODUCT_DISPLAY[productParam];
    const isRevoked = license?.status === 'revoked';
    const maintenanceExpired = isMaintenanceExpired(license?.maintenanceEnd);
    const isExistingActivation = intentParam === 'activate-existing' && !!deviceCodeParam;
    const activationName = productDisplay?.activationName || 'License';
    const pageTitle = isExistingActivation
        ? `Activate ${activationName} — License Portal — HForgeX`
        : `${activationName} License Portal — HForgeX`;

    usePortalPageMeta({
        title: pageTitle,
        description: isExistingActivation
            ? `Activate ${activationName} on this browser, register the device, and issue an activation code.`
            : `Manage your ${activationName} license, devices, and activation codes.`,
    });

    const [notification, setNotification] = useState(null);

    const showNotification = useCallback((type, message) => {
        setNotification({ type, message });
    }, []);

    async function handleAddDevice(deviceCode, deviceName) {
        try {
            await actions.addDevice(deviceCode, deviceName);
            showNotification('success', 'Device added successfully.');
        } catch {
            // Error is displayed via contextError below
        }
    }

    async function handleRemoveDevice(deviceCode) {
        try {
            await actions.removeDevice(deviceCode);
            showNotification('success', 'Device removed.');
        } catch {
            // Error is displayed via contextError below
        }
    }

    async function handleIssueBundle() {
        try {
            const data = await actions.issueBundle();
            return data;
        } catch {
            // Error is displayed via contextError below
            return null;
        }
    }

    async function handlePrepareActivationDevice() {
        if (!deviceCodeParam) return;
        const isRegistered = devices.some((device) => device.device_code === deviceCodeParam);
        if (isRegistered) return;
        await actions.addDevice(
            deviceCodeParam,
            `${productDisplay?.activationName || 'Extension'} device`
        );
        showNotification('success', 'Device registered. Creating activation code.');
    }

    function handleLogout() {
        actions.logout();
        navigate('/lookup', { replace: true });
    }

    // Valid session but no license data in memory (page reload / error recovery).
    // Can't re-fetch without email+licenseKey, so clear the stale session.
    // Use a ref to track if logout has been triggered, then redirect declaratively.
    const logoutTriggeredRef = useRef(false);
    useEffect(() => {
        if (!license && !logoutTriggeredRef.current) {
            logoutTriggeredRef.current = true;
            actions.logout();
        }
    }, [license, actions]);

    if (!license) {
        return <Navigate to="/lookup" replace />;
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* ── Context-level error ────────────────────────────── */}
            {contextError && (
                <div
                    className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30 px-4 py-3 rounded-lg"
                    role="alert"
                >
                    <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span className="flex-1">
                        {contextError.message || 'An error occurred. Please try again.'}
                    </span>
                    <button
                        type="button"
                        onClick={() => actions.clearError()}
                        className="ml-2 text-xs font-medium text-red-600 dark:text-red-400 hover:underline shrink-0"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {/* ── License Status Card ────────────────────────────── */}
            <section className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl p-5 space-y-4">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="flex items-center gap-2.5 mb-1">
                            <h2 className="text-lg font-semibold">License</h2>
                            <StatusBadge status={license.status} />
                        </div>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 font-mono">
                            {license.id}
                        </p>
                        {license.productId && (
                            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400 font-mono">
                                Product: {license.productId}
                            </p>
                        )}
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 uppercase">
                        {license.edition}
                    </span>
                </div>

                <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
                    <Calendar className="w-4 h-4" />
                    <span>
                        Update entitlement: <strong>{formatDate(license.maintenanceEnd)}</strong>
                        {maintenanceExpired && !isRevoked && (
                            <span className="ml-1 text-amber-600 dark:text-amber-400">
                                (expired)
                            </span>
                        )}
                    </span>
                </div>

                {/* Renewal prompt */}
                {maintenanceExpired && !isRevoked && (
                    <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 rounded-lg">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <div>
                            <p>
                                Your update entitlement has expired. New features released after
                                this date are not included.
                            </p>
                            <button
                                type="button"
                                onClick={() => navigate('/renew')}
                                className="mt-1 text-indigo-600 dark:text-indigo-400 hover:underline font-medium text-xs"
                            >
                                Renew Update Entitlement →
                            </button>
                        </div>
                    </div>
                )}

                {/* Revoked warning */}
                {isRevoked && (
                    <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30 px-4 py-3 rounded-lg">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <p>
                            This license has been revoked. Device management and bundle downloads
                            are disabled. Contact support if you believe this is an error.
                        </p>
                    </div>
                )}
            </section>

            {isExistingActivation && (
                <ExistingLicenseActivationPanel
                    productDisplay={productDisplay}
                    deviceCode={deviceCodeParam}
                    devices={devices}
                    isRevoked={isRevoked}
                    onPrepareDevice={handlePrepareActivationDevice}
                    onIssueBundle={handleIssueBundle}
                    trackingContext={{
                        productSlug: productParam || license?.productId?.split('/').pop() || '',
                        productName: activationName,
                        source,
                        feature,
                    }}
                />
            )}

            {/* ── Device List ────────────────────────────────────── */}
            <DeviceList
                devices={devices}
                maxDevices={license.maxDevices}
                onAddDevice={handleAddDevice}
                onRemoveDevice={handleRemoveDevice}
                isLoading={isLoading}
                isRevoked={isRevoked}
                initialDeviceCode={deviceCodeParam}
                autoOpenModal={!isExistingActivation && !!deviceCodeParam}
            />

            {/* ── Bundle Download ────────────────────────────────── */}
            {!isExistingActivation && (
                <BundleActions
                    onIssueBundle={handleIssueBundle}
                    hasDevices={devices.length > 0}
                    isRevoked={isRevoked}
                    activationTarget={
                        productDisplay?.extensionId
                            ? {
                                  extensionId: productDisplay.extensionId,
                                  productName: productDisplay.activationName || 'this extension',
                              }
                            : null
                    }
                    trackingContext={{
                        productSlug: productParam || license?.productId?.split('/').pop() || '',
                        productName: productDisplay?.activationName || 'this extension',
                        source,
                        feature,
                    }}
                />
            )}

            {/* ── Log out ────────────────────────────────────────── */}
            <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800">
                <button
                    type="button"
                    onClick={handleLogout}
                    className="inline-flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
                >
                    <LogOut className="w-4 h-4" />
                    Log out
                </button>
            </div>

            {/* ── Notification toast ─────────────────────────────── */}
            {notification && (
                <Notification
                    type={notification.type}
                    message={notification.message}
                    onDismiss={() => setNotification(null)}
                />
            )}
        </div>
    );
}
